import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import {
  ButtonComponent,
  CheckboxComponent,
  IconComponent,
  InputComponent,
  TooltipComponent,
  TypographyComponent,
} from '@purolator/ui-kit-angular';
import type { PackageSelectionConstraints } from '../../../../core/api/product-master/models/package-selection.model';
import { TranslatePipe } from '../../../../core/i18n';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import {
  customerDimensionExceedParams,
  type CustomerPackagingBannerErrors,
} from '../../customer-packaging-banner-errors';
import {
  blurCustomerPieceField,
  customerDimensionUnitLabelKey,
  customerPieceFieldErrorKey,
  customerPieceFieldHasError,
  customerWeightUnitLabelKey,
} from '../../customer-piece-field';
import {
  aggregateCustomerSpecialHandlingWarnings,
  isOverFiftyPoundWarning,
  type CustomerSpecialHandlingType,
} from '../../customer-special-handling';
import {
  CUSTOMER_MAX_TOTAL_PIECES,
  sumCustomerTotalPieces,
  sumCustomerTotalWeight,
  type PackagePieceRowGroup,
} from '../../forms/package-selection.form';
import { PackageMeasureInputDirective } from '../../package-measure-input.directive';
import type { PackageUnitSystem } from '../../package-types.config';
import { CustomerPackagingCard } from './customer-packaging-card/customer-packaging-card';

const EMPTY_BANNER_ERRORS: CustomerPackagingBannerErrors = {
  shipmentErrors: [],
  packageGroups: [],
  showSurchargeFooter: false,
  hasBlockingErrors: false,
};

/** Figma package cards (mobile + tablet ≤1199); desktop table mounts at ≥1200. */
const PACKAGE_CARDS_QUERY = '(max-width: 1199px)';

/**
 * Expanded Customer Packaging details: totals, unit toggle, piece table, and warnings.
 */
@Component({
  selector: 'app-customer-packaging-details',
  imports: [
    ReactiveFormsModule,
    AlertToast,
    ButtonComponent,
    CheckboxComponent,
    CustomerPackagingCard,
    IconComponent,
    InputComponent,
    PackageMeasureInputDirective,
    TooltipComponent,
    TypographyComponent,
    TranslatePipe,
  ],
  templateUrl: './customer-packaging-details.html',
  styleUrl: './customer-packaging-details.scss',
})
export class CustomerPackagingDetails {
  readonly pieces = input.required<FormArray<PackagePieceRowGroup>>();
  /** Live count so delete-row visibility updates when the FormArray mutates. */
  readonly pieceCount = input.required<number>();
  /** Per-row remount keys so Clear recreates puro-input hosts after reset. */
  readonly pieceRowViewKeys = input<readonly number[]>([0]);
  readonly unitSystem = input.required<PackageUnitSystem>();
  readonly constraints = input<PackageSelectionConstraints | null>(null);
  readonly showValidationErrors = input(false);
  /** Blocking red-banner model for Add/Save (shipment limits, field max, girth). */
  readonly bannerErrors = input<CustomerPackagingBannerErrors>(EMPTY_BANNER_ERRORS);
  /** Params for dual-unit weight exceed / total-weight messages. */
  readonly weightLimitParams = input<Record<string, string>>({});
  /** Params for dual-unit dimension exceed messages. */
  readonly lengthLimitParams = input<Record<string, string>>({});
  /** Destination residential (service-ability / ship-to) for SH10 evaluation. */
  readonly destinationResidential = input(false);
  readonly unitSystemChange = output<PackageUnitSystem>();
  readonly addPackage = output<void>();
  readonly clearRow = output<number>();
  readonly removeRow = output<number>();

  /**
   * True when the Figma card layout (≤1199) should render instead of the desktop table.
   * Only one layout mounts inputs so FormControl CVAs are not duplicated.
   */
  protected readonly usePackageCards = signal(false);

  /**
   * Bumps when piece-row values change so totals and add-button visibility re-read FormControls
   * (controls are not signals).
   */
  private readonly piecesRevision = signal(0);

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const query = window.matchMedia(PACKAGE_CARDS_QUERY);
      this.usePackageCards.set(query.matches);
      const onChange = (event: MediaQueryListEvent) => this.usePackageCards.set(event.matches);
      query.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));
    }

    effect((onCleanup) => {
      const pieces = this.pieces();
      this.piecesRevision.update((value) => value + 1);

      const subscription = pieces.valueChanges.subscribe(() => {
        this.piecesRevision.update((value) => value + 1);
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  /**
   * Stable track key for a piece row; bumps when Clear remounts puro-input hosts.
   */
  protected pieceRowTrackKey(index: number): string {
    return `${index}-${this.pieceRowViewKeys()[index] ?? 0}`;
  }

  /**
   * Sums identical piece counts across all package rows (numeric; coerces string inputs).
   */
  protected totalPieces(): number {
    this.piecesRevision();
    return sumCustomerTotalPieces(this.pieces());
  }

  /**
   * Whether another package row can be added without exceeding the total-pieces limit.
   * Hides "+ Add another package" once Total Pieces reaches the configured maximum.
   */
  protected canAddAnotherPackage(): boolean {
    return this.totalPieces() < CUSTOMER_MAX_TOTAL_PIECES;
  }

  /**
   * Sums weight across all package rows (identical pieces × per-piece weight).
   */
  protected totalWeight(): number {
    this.piecesRevision();
    return sumCustomerTotalWeight(this.pieces());
  }

  /**
   * Formats total weight for display with up to two decimal places.
   */
  protected formattedTotalWeight(): string {
    return this.totalWeight().toFixed(2);
  }

  /**
   * True when the red validation banner should render.
   */
  protected showErrorBanner(): boolean {
    const banner = this.bannerErrors();
    return (
      this.showValidationErrors() &&
      (banner.shipmentErrors.length > 0 ||
        banner.packageGroups.length > 0 ||
        banner.showSurchargeFooter)
    );
  }

  /**
   * Returns translate params for a banner message key.
   */
  protected bannerMessageParams(key: string): Record<string, string> | undefined {
    if (key.includes('totalPiecesExceedsLimit')) {
      return { maxPieces: String(CUSTOMER_MAX_TOTAL_PIECES) };
    }

    if (key.includes('perPieceWeightExceedsMax')) {
      return this.weightLimitParams();
    }

    if (
      key.includes('perPieceLengthExceedsMax') ||
      key.includes('perPieceWidthExceedsMax') ||
      key.includes('perPieceHeightExceedsMax')
    ) {
      return this.lengthLimitParams();
    }

    if (key.includes('overallDimensionExceedsMax')) {
      return this.girthLimitParams();
    }

    return undefined;
  }

  /**
   * Dual-unit params for overall girth banner copy from Product Master `girth.max`.
   */
  private girthLimitParams(): Record<string, string> | undefined {
    const maxGirthIn = this.constraints()?.girth?.max;
    if (typeof maxGirthIn !== 'number' || !Number.isFinite(maxGirthIn)) {
      return undefined;
    }

    return customerDimensionExceedParams(maxGirthIn);
  }

  /**
   * Switches between imperial and metric measurement units.
   */
  protected onUnitToggle(system: PackageUnitSystem): void {
    this.unitSystemChange.emit(system);
  }

  /**
   * Weight unit label for the Per Piece Weight column header.
   */
  protected weightUnitLabelKey(): string {
    return customerWeightUnitLabelKey(this.unitSystem());
  }

  /**
   * Dimension unit label for the Dimension column header.
   */
  protected dimensionUnitLabelKey(): string {
    return customerDimensionUnitLabelKey(this.unitSystem());
  }

  /**
   * Returns whether a control should show an error state (touched or after Add/Save).
   */
  protected hasError(
    rowIndex: number,
    controlName: keyof PackagePieceRowGroup['controls'],
  ): boolean {
    return customerPieceFieldHasError(
      this.pieces().at(rowIndex).controls[controlName],
      this.showValidationErrors(),
    );
  }

  /**
   * Maps field validation errors to the inline message key.
   */
  protected fieldErrorKey(
    rowIndex: number,
    controlName: keyof PackagePieceRowGroup['controls'],
  ): string {
    return customerPieceFieldErrorKey(this.pieces().at(rowIndex).controls[controlName]);
  }

  /**
   * Marks a control touched on blur so Required / Exceeds limit appear.
   */
  protected onFieldBlur(
    rowIndex: number,
    controlName: keyof PackagePieceRowGroup['controls'],
  ): void {
    blurCustomerPieceField(this.pieces().at(rowIndex).controls[controlName], controlName);
  }

  /**
   * True when any row's per-piece weight exceeds the 50 lb surcharge warning threshold.
   */
  protected showOverFiftyWarning(): boolean {
    this.piecesRevision();
    return this.pieces().controls.some((row) =>
      isOverFiftyPoundWarning(row.controls.perPieceWeight.value, this.unitSystem()),
    );
  }

  /**
   * Aggregates per-piece hierarchy winners (OM-138) across all package rows.
   */
  protected specialHandlingTypes(): CustomerSpecialHandlingType[] {
    this.piecesRevision();
    this.destinationResidential();

    return aggregateCustomerSpecialHandlingWarnings(
      this.pieces().controls.map((row) => ({
        identicalPieces: row.controls.identicalPieces.value,
        perPieceWeight: row.controls.perPieceWeight.value,
        length: row.controls.length.value,
        width: row.controls.width.value,
        height: row.controls.height.value,
        requiresAdditionalHandling: row.controls.requiresAdditionalHandling.value,
      })),
      this.unitSystem(),
      { destinationResidential: this.destinationResidential() },
    );
  }

  /**
   * i18n key for a special-handling warning type.
   */
  protected specialHandlingMessageKey(type: CustomerSpecialHandlingType): string {
    return `createShipment.packageStep.details.specialHandling.${type}`;
  }
}
